// src/components/common/MultiSelectList.tsx
import React, { useState } from 'react';
import './MultiSelectList.css';

// Interface genérica para itens da lista, com ID tipado
interface SelectItem<IDType> {
  id: IDType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Permite propriedades adicionais (bag genérico)
}

// Interface para as props do componente, com IDType genérico
interface MultiSelectListProps<T extends SelectItem<IDType>, IDType> {
  items: T[];
  selectedIds: IDType[];
  handleChange: (id: IDType) => void;
  theme: 'dark' | 'light';
  label: string; // Texto do label principal
  displayFields: { key: keyof T; label: string }[]; // Campos a exibir (ex.: codFilial, nomeFantasia)
  disabled?: boolean; // Adicionado para suportar desabilitação
}

// Componente genérico de lista suspensa com multiseleção
const MultiSelectList = <T extends SelectItem<IDType>, IDType>({
  items,
  selectedIds,
  handleChange,
  theme,
  label,
  displayFields,
  disabled,
}: MultiSelectListProps<T, IDType>) => {
  // Estado para controlar a abertura do dropdown
  const [isOpen, setIsOpen] = useState(false);

  // Contagem de itens selecionados
  const selectedCount = selectedIds.length;

  // Alterna a visibilidade do dropdown, se não estiver desabilitado
  const toggleDropdown = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return;
    e.preventDefault(); // Evita comportamento padrão do navegador
    e.stopPropagation(); // Impede propagação para outros elementos
    setIsOpen((prev) => !prev);
  };

  // Fecha o dropdown ao clicar fora, se não estiver desabilitado
  const handleClickOutside = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setIsOpen(false);
  };

  // Manipula clique no checkbox, evitando propagação para a linha
  const handleCheckboxChange = (id: IDType, e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation(); // Impede que o clique no checkbox acione o clique da linha
    handleChange(id);
  };

  // Manipula clique na linha, exceto no checkbox
  const handleItemClick = (id: IDType, e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Verifica se o clique foi diretamente no checkbox ou em seu descendente
    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
      return; // Ignora se o clique foi no checkbox
    }
    handleChange(id);
  };

  return (
    <div className={`multi-select-list ${theme} ${disabled ? 'disabled' : ''}`}>
      <label>
        {label}:
        <div className="dropdown-container">
          <div
            className="dropdown-toggle"
            onClick={toggleDropdown}
            onKeyDown={(e) => e.key === 'Enter' && toggleDropdown(e)}
            role="button"
            tabIndex={disabled ? -1 : 0} // Desabilita navegação por teclado
          >
            Total selecionado: {selectedCount}
          </div>
          {isOpen && !disabled && (
            <div className="dropdown-menu">
              {items.map((item) => (
                <div
                  key={String(item.id)}
                  className="dropdown-item"
                  onClick={(e) => handleItemClick(item.id, e)}
                  role="option"
                  aria-selected={selectedIds.includes(item.id)}
                >
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={(e) => handleCheckboxChange(item.id, e)}
                      disabled={disabled} // Desabilita checkboxes
                    />
                    {displayFields.map((field, index) => (
                      <span key={field.key as string}>
                        {field.label}: {item[field.key]}
                        {/* Separador com NBSP (U+00A0) nos dois lados do pipe: como .checkbox-label
                            é flex, cada <span> é flex item e o espaço normal ao fim
                            seria removido (trailing-whitespace) → "3 |Nome". NBSP não
                            é white-space p/ o CSS, então sobrevive. */}
                        {index < displayFields.length - 1 && ' | '}
                      </span>
                    ))}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </label>
      {isOpen && !disabled && <div className="overlay" onClick={handleClickOutside} />}
    </div>
  );
};

export default MultiSelectList;